// META-PREMIO (2026-07-16, v3): termômetro da meta anual + prêmio geral —
// aba "🎯 Meta & Prêmio" do hub DRE & Resultado, visível a TODOS os usuários
// (perm financeiro.meta.ver, concedida a todos os perfis).
// Regra (Lucas): "a lógica é o LUCRO, não a receita (a receita é proxy)".
// Referência (100%) = SUPERMETA em lucro (R$ 1,12M = 20% de 5,6M); a meta
// são 80% dela (~R$ 896k ≈ 4,5M em receita) e destrava 0,60 salário.
// Régua da planilha 2025 (interpolação linear entre degraus; ≥140% trava):
//   80%→0,60 · 90%→0,70 · 100%→1,00 · 110%→1,10 · 120%→1,30 · 130%→1,40 ·
//   140%→1,50 (teto — não sobe mais).
import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Progress, Statistic, Row, Col, Alert, Typography, Spin, Tag, Table, InputNumber,
} from 'antd';
import { TrophyOutlined, RiseOutlined, AimOutlined } from '@ant-design/icons';
import { API_URL } from '../config';

const { Text, Title } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface MetaPremioData {
  ano: number;
  mes_ate: number;
  // Meses fechados mandam no número-chefe; o corrente vem à parte (parcial).
  mes_parcial?: {
    periodo: string; receita_bruta: number; lucro_liquido: number;
    // Por que o mês em andamento aparece negativo — ver comentário no
    // dre-engine: despesa é provisionada pro mês cheio, receita só é
    // reconhecida quando fatura.
    receita_prevista_mes?: number; despesa_provisionada?: number;
    despesa_paga?: number; dia_do_mes?: number; dias_no_mes?: number;
    pct_do_mes?: number;
  } | null;
  receita_bruta_ytd: number;
  lucro_liquido_ytd: number;
  margem_ytd: number;
  por_mes: { periodo: string; receita_bruta: number; lucro_liquido: number; parcial?: boolean }[];
  metas: {
    ano: number; supermeta_lucro: number; supermeta_receita: number;
    meta_lucro: number; meta_receita: number; lucro_alvo: number;
    premio_teto: number; regua: [number, number][];
  };
  premio: {
    base: string;
    atingimento: number;
    salarios_atual: number;
    falta_para_meta?: number;
    teto: number;
    regra: string;
  };
  projecao: {
    dia_do_ano: number;
    receita_projetada: number;
    lucro_projetado: number;
    atingimento_projetado: number;
    salarios_projetados: number;
  } | null;
}

const fmtR$ = (v: number) =>
  'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (v: number, dec = 1) => ((v || 0) * 100).toFixed(dec) + '%';
const fmtSal = (v: number) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Chave do simulador de salário — fica SÓ no navegador da pessoa
// (localStorage), nunca é enviado pro sistema (pedido do Lucas 2026-07-16).
const CHAVE_SALARIO = 'meta_premio_meu_salario';

export default function MetaPremio() {
  const [dados, setDados] = useState<MetaPremioData | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salario, setSalario] = useState<number | null>(() => {
    try {
      const v = localStorage.getItem(CHAVE_SALARIO);
      return v ? parseFloat(v) : null;
    } catch { return null; }
  });
  const aoMudarSalario = (v: number | null) => {
    setSalario(v ?? null);
    try {
      if (v && v > 0) localStorage.setItem(CHAVE_SALARIO, String(v));
      else localStorage.removeItem(CHAVE_SALARIO);
    } catch { /* modo privado etc. */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API_URL}/dre/meta-premio`, {
          headers: { Authorization: token },
        });
        setDados(res.data);
      } catch (e: any) {
        setErro(e?.response?.data?.erro || e.message || 'Falha ao carregar');
      }
    })();
  }, []);

  if (erro) return <Alert type="error" message="Não consegui carregar a meta" description={erro} style={{ margin: 24 }} />;
  if (!dados) return <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" tip="Calculando o ano até aqui..." /></div>;

  const { metas, premio, projecao } = dados;
  // Quanto falta EM REAIS pro prêmio destravar. O backend manda calculado;
  // o fallback cobre o período em que a Lambda antiga ainda estiver no ar.
  const falta = premio.falta_para_meta != null
    ? premio.falta_para_meta
    : Math.max(0, metas.meta_lucro - dados.lucro_liquido_ytd);
  const parcialMes = dados.mes_parcial;
  const lucro = dados.lucro_liquido_ytd;
  const regua = metas.regua || [];
  const pctSupermeta = Math.max(0, Math.min(150, premio.atingimento * 100));
  const pctReceitaProxy = Math.min(100, (dados.receita_bruta_ytd / metas.supermeta_receita) * 100);
  const anoFrac = projecao ? projecao.dia_do_ano / 365 : dados.mes_ate / 12;

  // Degrau da régua onde o atingimento ATUAL e o PROJETADO caem (pra destacar)
  const degrauDe = (ating: number) => {
    let idx = -1;
    regua.forEach((p, i) => { if (ating >= p[0]) idx = i; });
    return idx;
  };
  const degrauAtual = degrauDe(premio.atingimento);
  const degrauProj = projecao ? degrauDe(projecao.atingimento_projetado) : -1;

  const colReguaRows = regua.map((p, i) => ({
    key: i,
    atingimento: fmtPct(p[0], 0) + (i === regua.length - 1 ? ' (teto)' : ''),
    lucro_equiv: fmtR$(p[0] * metas.supermeta_lucro),
    salarios: fmtSal(p[1]) + ' salário',
    _mult: p[1],
    _atual: i === degrauAtual,
    _proj: i === degrauProj,
  }));

  const colunasMes = [
    { title: 'Mês', dataIndex: 'periodo', key: 'p',
      render: (v: string, r: any) => <>{v}{r.parcial && <Tag style={{ marginLeft: 6 }}>parcial</Tag>}</> },
    { title: 'Receita bruta', dataIndex: 'receita_bruta', key: 'r', align: 'right' as const,
      render: (v: number, r: any) => <Text type={r.parcial ? 'secondary' : undefined}>{fmtR$(v)}</Text> },
    { title: 'Lucro líquido', dataIndex: 'lucro_liquido', key: 'l', align: 'right' as const,
      render: (v: number, r: any) => <Text type={v < 0 ? 'danger' : r.parcial ? 'secondary' : undefined}>{fmtR$(v)}</Text> },
  ];

  return (
    <div style={{ padding: 16, maxWidth: 1100 }}>
      <Title level={4} style={{ marginTop: 4 }}>
        <TrophyOutlined /> Meta {dados.ano} & Prêmio geral
      </Title>
      <Text type="secondary">
        O prêmio de todos anda pelo <b>lucro do ano</b>. A referência (100%) é a supermeta
        de lucro de <b>{fmtR$(metas.supermeta_lucro)}</b> (equivale a {fmtR$(metas.supermeta_receita)} de
        receita com margem de {fmtPct(metas.lucro_alvo, 0)}). A meta são <b>{fmtPct(regua[0]?.[0] || 0.8, 0)} disso</b>
        ({fmtR$(metas.meta_lucro)} de lucro ≈ {fmtR$(metas.meta_receita)} de receita) e destrava
        <b> {fmtSal(regua[0]?.[1] || 0.6)} salário</b>. Daí a régua sobe até o teto de
        <b> {fmtSal(metas.premio_teto)} salário</b> em {fmtPct(regua[regua.length - 1]?.[0] || 1.4, 0)} — a partir daí não sobe mais.
      </Text>

      {/* ── PLACAR (revisão 2026-08-14) ──────────────────────────────────
          A ordem aqui é deliberada. A versão anterior abria com "79,7% da
          supermeta · prêmio hoje: 0 salário" e a equipe lia isso como "o ano
          está ruim" — quando faltavam R$ 3.478 de R$ 896.000 e a empresa
          tinha feito em 7 meses quase a meta do ano INTEIRO. O placar parcial
          estava sendo lido como resultado final.
          Agora: primeiro o que já foi feito, depois quanto falta em REAIS,
          depois onde o ano fecha no ritmo. O "travado hoje" desce para nota
          de rodapé, explicado. */}
      <Card size="small" style={{ marginTop: 16 }}
        title={<><AimOutlined /> Lucro líquido {dados.ano} — meses fechados, até {String(dados.mes_ate).padStart(2, '0')}/{dados.ano} (a métrica do prêmio)</>}>
        <Row gutter={[16, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} md={8}>
            <Statistic title={`Lucro no ano (até ${String(dados.mes_ate).padStart(2, '0')}/${dados.ano})`}
              value={fmtR$(lucro)} valueStyle={{ color: '#020D7F' }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              margem {fmtPct(dados.margem_ytd)} · referência {fmtPct(metas.lucro_alvo, 0)}
            </Text>
          </Col>
          <Col xs={24} md={8}>
            {falta > 0 ? (
              <>
                <Statistic title="Falta para destravar o prêmio" value={fmtR$(falta)}
                  valueStyle={{ color: falta <= lucro * 0.05 ? '#B07500' : undefined }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  a meta do ANO é {fmtR$(metas.meta_lucro)} — e ainda faltam {12 - dados.mes_ate} meses
                </Text>
              </>
            ) : (
              <>
                <Statistic title="Prêmio já destravado" value={fmtSal(premio.salarios_atual)}
                  suffix="salário" valueStyle={{ color: '#52c41a' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>a meta do ano já foi superada</Text>
              </>
            )}
          </Col>
          <Col xs={24} md={8}>
            {projecao && (
              <>
                <Statistic title="No ritmo atual, o ano fecha em"
                  value={fmtPct(projecao.atingimento_projetado, 0)}
                  suffix={`→ ${fmtSal(projecao.salarios_projetados)} salário`}
                  prefix={<RiseOutlined />}
                  valueStyle={{ color: projecao.salarios_projetados > 0 ? '#52c41a' : undefined }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  projeção pelo ritmo dos meses fechados
                </Text>
              </>
            )}
          </Col>
        </Row>
        <Row gutter={[24, 8]} align="middle">
          <Col xs={24} md={16}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>{fmtR$(lucro)}</Text>
              <Text type="secondary"> — atingimento de <b>{fmtPct(premio.atingimento)}</b> da supermeta ({fmtR$(metas.supermeta_lucro)})</Text>
            </div>
            <Progress percent={Math.round(pctSupermeta)} strokeColor={pctSupermeta >= 100 ? '#faad14' : pctSupermeta >= 80 ? '#52c41a' : '#1677ff'}
              format={(p) => `${p}%`} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Marcos: {fmtPct(regua[0]?.[0] || 0.8, 0)} = meta (destrava o prêmio) · 100% = supermeta · {fmtPct(regua[regua.length - 1]?.[0] || 1.4, 0)} = teto
            </Text>
          </Col>
          <Col xs={24} md={8}>
            <Statistic title={`Ritmo do ano (${fmtPct(anoFrac, 0)} decorrido)`}
              value={fmtPct(premio.atingimento)} suffix="da supermeta"
              prefix={<RiseOutlined />} />
            <div style={{ marginTop: 8 }}>
              Margem atual: <Tag color={dados.margem_ytd >= metas.lucro_alvo ? 'green' : 'orange'}>{fmtPct(dados.margem_ytd)}</Tag>
              <Text type="secondary">referência {fmtPct(metas.lucro_alvo, 0)}</Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Prêmio + proxy da receita + projeção */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="Travado hoje (foto dos meses fechados)"
              value={fmtSal(premio.salarios_atual)} suffix="salário"
              valueStyle={{ color: premio.salarios_atual > 0 ? '#52c41a' : undefined }} />
            {premio.salarios_atual === 0 && (
              <Text type="secondary">
                Destrava em {fmtR$(metas.meta_lucro)} de lucro no ano — faltam <b>{fmtR$(falta)}</b>.
                Este número só conta meses <b>fechados</b>; não é o resultado do ano.
              </Text>
            )}
            {salario != null && salario > 0 && premio.salarios_atual > 0 && (
              <div style={{ marginTop: 6 }}>
                <Text>Pra você: <Text strong style={{ color: '#B07500' }}>{fmtR$(premio.salarios_atual * salario)}</Text></Text>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic title="Receita bruta no ano (proxy da meta)" value={fmtR$(dados.receita_bruta_ytd)} />
            <div style={{ marginTop: 8 }}>
              <Progress percent={Math.round(pctReceitaProxy)} size="small"
                format={(p) => `${p}% dos ${fmtR$(metas.supermeta_receita)}`} />
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            {projecao ? (
              <>
                <Statistic title="Lucro projetado fim de ano (ritmo dos meses fechados)" value={fmtR$(projecao.lucro_projetado)} />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Atingimento projetado {fmtPct(projecao.atingimento_projetado)} → </Text>
                  <Tag color={projecao.salarios_projetados > 0 ? 'green' : 'default'}>
                    {fmtSal(projecao.salarios_projetados)} salário
                  </Tag>
                  {salario != null && salario > 0 && projecao.salarios_projetados > 0 && (
                    <Text> ≈ <Text strong style={{ color: '#B07500' }}>{fmtR$(projecao.salarios_projetados * salario)}</Text> pra você</Text>
                  )}
                </div>
              </>
            ) : <Text type="secondary">Sem projeção pra este ano.</Text>}
          </Card>
        </Col>
      </Row>

      {/* Simulador pessoal (Lucas 2026-07-16): o salário fica SÓ no navegador
          da pessoa (localStorage) — nunca é enviado pro sistema. */}
      <Card size="small" style={{ marginTop: 16, borderColor: '#d9b96c40' }}>
        <Row gutter={[16, 8]} align="middle">
          <Col xs={24} md={10}>
            <Text strong>💰 Simule com o seu salário</Text>
            <div><Text type="secondary" style={{ fontSize: 12 }}>
              Fica salvo só neste navegador — não é enviado pro sistema.
            </Text></div>
          </Col>
          <Col xs={24} md={8}>
            <InputNumber
              value={salario ?? undefined}
              onChange={aoMudarSalario}
              min={0} step={100} decimalSeparator=","
              prefix="R$" placeholder="Seu salário mensal"
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={6}>
            {salario != null && salario > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Meta = <Text strong style={{ color: '#B07500' }}>{fmtR$((regua[0]?.[1] || 0.6) * salario)}</Text> ·
                teto = <Text strong style={{ color: '#B07500' }}>{fmtR$(metas.premio_teto * salario)}</Text>
              </Text>
            )}
          </Col>
        </Row>
      </Card>

      {/* A régua (planilha), com a posição atual e a projetada destacadas */}
      <Card size="small" style={{ marginTop: 16 }} title="A régua do prêmio (por atingimento do lucro)">
        <Table
          dataSource={colReguaRows}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          onRow={(r: any) => ({
            style: r._atual
              ? { background: '#f6ffed', fontWeight: 600 }
              : r._proj ? { background: '#e6f4ff' } : {},
          })}
          columns={[
            { title: 'Atingimento', dataIndex: 'atingimento', key: 'a' },
            { title: 'Lucro equivalente', dataIndex: 'lucro_equiv', key: 'l', align: 'right' as const },
            { title: 'Prêmio', dataIndex: 'salarios', key: 's', align: 'right' as const },
            ...(salario != null && salario > 0 ? [{
              title: 'Pra você', dataIndex: '_mult', key: 'v', align: 'right' as const,
              render: (m: number) => <Text strong style={{ color: '#B07500' }}>{fmtR$(m * salario)}</Text>,
            }] : []),
            {
              title: '', key: 'flag', render: (_: any, r: any) => (
                <>
                  {r._atual && <Tag color="green">hoje</Tag>}
                  {r._proj && <Tag color="blue">projeção</Tag>}
                </>
              ),
            },
          ]}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Entre os degraus o prêmio cresce proporcionalmente (interpolação linear). Do último degrau
          em diante não sobe mais. Apuração final no fechamento do ano.
        </Text>
      </Card>

      <Card size="small" style={{ marginTop: 16 }} title="Mês a mês">
        {/* O mês em andamento SEMPRE aparece negativo, e não é erro: a
            despesa é provisionada pro mês inteiro assim que o compromisso é
            conhecido, enquanto a receita só é reconhecida quando fatura — e
            o faturamento sai no fim do mês. Sem esta explicação, a equipe lia
            o negativo como prejuízo real (motivo desta revisão, 14/08/2026). */}
        {parcialMes && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={`${parcialMes.periodo} ainda está em andamento${
              parcialMes.pct_do_mes ? ` — ${fmtPct(parcialMes.pct_do_mes, 0)} do mês decorrido` : ''
            }`}
            description={
              <div style={{ fontSize: 12 }}>
                <div>
                  <b>Receita:</b> {fmtR$(parcialMes.receita_bruta)} reconhecidos
                  {parcialMes.receita_prevista_mes ? <> de {fmtR$(parcialMes.receita_prevista_mes)} previstos no mês</> : null}
                  {' '}— o faturamento sai no fim do mês.
                </div>
                {parcialMes.despesa_provisionada != null && (
                  <div>
                    <b>Despesa:</b> {fmtR$(parcialMes.despesa_provisionada)} lançados, dos quais
                    {' '}<b>{fmtR$(parcialMes.despesa_paga || 0)}</b> já saíram do caixa
                    {' '}— o resto é provisão do mês.
                  </div>
                )}
                <div style={{ marginTop: 6 }}>
                  Por isso o mês aparece negativo. <b>Ele não entra na conta do prêmio</b> —
                  o placar acima usa só meses fechados.
                </div>
              </div>
            } />
        )}
        <Table dataSource={dados.por_mes.map((m, i) => ({ ...m, key: i }))}
          columns={colunasMes} pagination={false} size="small"
          scroll={{ x: true }} />
      </Card>
    </div>
  );
}
